// ============================================
// 오링왕국 - 글로벌 랭킹 API 서비스
// ============================================

export interface GlobalRankingEntry {
    player_name: string;
    score: number;
    money: number;
    happiness: number;
    population: number;
    ending: string;
    mbti: string;
    difficulty: 'easy' | 'hard';
    created_at?: string;
}

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
}

// Cloudflare Pages Functions 사용 (같은 도메인)
const API_BASE_URL = '/api';

/**
 * 글로벌 랭킹 조회
 */
export async function fetchGlobalRankings(
    difficulty: 'easy' | 'hard',
    limit: number = 20
): Promise<GlobalRankingEntry[]> {
    try {
        const response = await fetch(
            `${API_BASE_URL}/rankings?difficulty=${difficulty}&limit=${limit}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result: ApiResponse<GlobalRankingEntry[]> = await response.json();

        if (result.success && result.data) {
            return result.data;
        }

        return [];
    } catch (error) {
        console.error('Failed to fetch global rankings:', error);
        return [];
    }
}

/**
 * 글로벌 랭킹 등록
 */
export async function submitGlobalRanking(
    entry: Omit<GlobalRankingEntry, 'created_at'>
): Promise<{ success: boolean; rank?: number }> {
    try {
        const response = await fetch(`${API_BASE_URL}/rankings`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(entry)
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result: ApiResponse<{ rank: number }> = await response.json();

        if (result.success && result.data) {
            return { success: true, rank: result.data.rank };
        }

        return { success: false };
    } catch (error) {
        console.error('Failed to submit global ranking:', error);
        return { success: false };
    }
}

/**
 * 상위 랭킹 조회 (이지/하드 동시)
 */
export async function fetchTopRankings(): Promise<{
    easy: GlobalRankingEntry[];
    hard: GlobalRankingEntry[];
}> {
    try {
        const [easy, hard] = await Promise.all([
            fetchGlobalRankings('easy', 20),
            fetchGlobalRankings('hard', 20)
        ]);
        return { easy, hard };
    } catch (error) {
        console.error('Failed to fetch top rankings:', error);
        return { easy: [], hard: [] };
    }
}
